import { format, eachDayOfInterval } from 'date-fns';
import type { Timesheet, Employee, Project } from '@/types';
import type { ProjectDetailData, ProjectUtilisation } from '@/types/utilisation';

/**
 * Calculate detailed project data for modal
 */
export const calculateProjectDetailData = (
  project: ProjectUtilisation,
  timesheets: Timesheet[],
  employees: Employee[],
  projects: Project[],
  dateRange: { start: Date; end: Date }
): ProjectDetailData => {
  const projectData = projects.find((p) => p.id === project.projectId);

  // Filter timesheets for this project
  const projectTimesheets = timesheets.filter(
    (ts) =>
      ts.status !== 'draft' &&
      ts.status !== 'submitted' &&
      new Date(ts.weekEnding) >= dateRange.start &&
      new Date(ts.weekEnding) <= dateRange.end &&
      ts.entries.some((entry) => entry.projectId === project.projectId)
  );

  // Calculate employee allocation
  const employeeAllocation = calculateEmployeeAllocation(
    projectTimesheets,
    project.projectId,
    employees
  );

  // Calculate weekly trend
  const weeklyTrend = calculateWeeklyTrend(projectTimesheets, project.projectId, projectData);

  // Calculate timeline
  const timeline = calculateTimeline(projectTimesheets, project.projectId, dateRange);

  return {
    project,
    employeeAllocation,
    weeklyTrend,
    timeline,
  };
};

/**
 * Calculate employee allocation
 */
const calculateEmployeeAllocation = (
  timesheets: Timesheet[],
  projectId: string,
  employees: Employee[]
): ProjectDetailData['employeeAllocation'] => {
  const employeeMap = new Map<string, { hours: number; employeeName: string }>();

  timesheets.forEach((timesheet) => {
    const employee = employees.find((e) => e.id === timesheet.employeeId);
    if (!employee) return;

    timesheet.entries.forEach((entry) => {
      if (entry.projectId !== projectId) return;

      if (!employeeMap.has(timesheet.employeeId)) {
        employeeMap.set(timesheet.employeeId, {
          hours: 0,
          employeeName: employee.name,
        });
      }

      const employeeData = employeeMap.get(timesheet.employeeId)!;
      // Sum all hours from the hours object
      const hours = Object.values(entry.hours).reduce((sum, h) => sum + h, 0);
      employeeData.hours += hours;
    });
  });

  const totalHours = Array.from(employeeMap.values()).reduce(
    (sum, emp) => sum + emp.hours,
    0
  );

  return Array.from(employeeMap.values())
    .map((emp) => ({
      employeeName: emp.employeeName,
      hours: emp.hours,
      percentage: totalHours > 0 ? (emp.hours / totalHours) * 100 : 0,
    }))
    .sort((a, b) => b.hours - a.hours);
};

/**
 * Calculate weekly trend
 */
const calculateWeeklyTrend = (
  timesheets: Timesheet[],
  projectId: string,
  project?: Project
): ProjectDetailData['weeklyTrend'] => {
  const weeklyMap = new Map<
    string,
    { totalHours: number; billableHours: number; weekEnding: Date }
  >();

  timesheets.forEach((timesheet) => {
    const weekKey = format(new Date(timesheet.weekEnding), 'yyyy-MM-dd');

    if (!weeklyMap.has(weekKey)) {
      weeklyMap.set(weekKey, {
        totalHours: 0,
        billableHours: 0,
        weekEnding: new Date(timesheet.weekEnding),
      });
    }

    const weekData = weeklyMap.get(weekKey)!;

    timesheet.entries.forEach((entry) => {
      if (entry.projectId !== projectId) return;

      // Sum all hours from the hours object
      const hours = Object.values(entry.hours).reduce((sum, h) => sum + h, 0);
      weekData.totalHours += hours;
      if (project?.billable) {
        weekData.billableHours += hours;
      }
    });
  });

  return Array.from(weeklyMap.values())
    .sort((a, b) => a.weekEnding.getTime() - b.weekEnding.getTime())
    .map((week) => ({
      week: format(week.weekEnding, 'MMM d'),
      totalHours: week.totalHours,
      billableHours: week.billableHours,
    }));
};

/**
 * Calculate timeline
 */
const calculateTimeline = (
  timesheets: Timesheet[],
  projectId: string,
  dateRange: { start: Date; end: Date }
): ProjectDetailData['timeline'] => {
  const dailyMap = new Map<string, { hours: number; date: Date }>();

  // Initialize all days in range
  const days = eachDayOfInterval({ start: dateRange.start, end: dateRange.end });
  days.forEach((day) => {
    const dayKey = format(day, 'yyyy-MM-dd');
    dailyMap.set(dayKey, {
      hours: 0,
      date: day,
    });
  });

  // Aggregate timesheet data
  timesheets.forEach((timesheet) => {
    const weekEnd = new Date(timesheet.weekEnding);
    // Calculate week start (Monday)
    const weekStart = new Date(weekEnd);
    weekStart.setDate(weekEnd.getDate() - 6);

    timesheet.entries.forEach((entry) => {
      if (entry.projectId !== projectId) return;

      // Process each day's hours from the hours object
      const dayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
      dayNames.forEach((dayName, dayIndex) => {
        const dayHours = entry.hours[dayName];
        if (dayHours > 0) {
          const entryDate = new Date(weekStart);
          entryDate.setDate(weekStart.getDate() + dayIndex);
          const dayKey = format(entryDate, 'yyyy-MM-dd');

          if (dailyMap.has(dayKey)) {
            const dayData = dailyMap.get(dayKey)!;
            dayData.hours += dayHours;
          }
        }
      });
    });
  });

  return Array.from(dailyMap.values())
    .sort((a, b) => a.date.getTime() - b.date.getTime())
    .filter((day) => day.hours > 0); // Only include days with hours
};

/**
 * Export project-specific report as CSV
 */
export const exportProjectReportCSV = (data: ProjectDetailData): string => {
  const lines: string[] = [];

  // Header
  lines.push(`Project Utilisation Report - ${data.project.projectName}`);
  lines.push(`Client: ${data.project.clientName}`);
  lines.push(`Generated: ${format(new Date(), 'yyyy-MM-dd HH:mm')}`);
  lines.push('');

  // Summary
  lines.push('Summary');
  lines.push('Metric,Value');
  lines.push(`Total Hours,${data.project.totalHours.toFixed(2)}`);
  lines.push(`Billable Hours,${data.project.billableHours.toFixed(2)}`);
  lines.push(
    `Non-Billable Hours,${(data.project.totalHours - data.project.billableHours).toFixed(2)}`
  );
  lines.push(`Utilisation Rate,${(data.project.utilisationRate * 100).toFixed(1)}%`);
  lines.push(`Employee Count,${data.project.employeeCount}`);
  lines.push(`Billable Project,${data.project.isBillable ? 'Yes' : 'No'}`);
  lines.push('');

  // Employee Allocation
  lines.push('Employee Allocation');
  lines.push('Employee,Hours,Percentage');
  data.employeeAllocation.forEach((emp) => {
    lines.push(`${emp.employeeName},${emp.hours.toFixed(2)},${emp.percentage.toFixed(1)}%`);
  });
  lines.push('');

  // Weekly Trend
  lines.push('Weekly Trend');
  lines.push('Week,Total Hours,Billable Hours');
  data.weeklyTrend.forEach((week) => {
    lines.push(`${week.week},${week.totalHours.toFixed(2)},${week.billableHours.toFixed(2)}`);
  });
  lines.push('');

  // Timeline
  lines.push('Daily Timeline');
  lines.push('Date,Hours');
  data.timeline.forEach((day) => {
    lines.push(`${format(day.date, 'yyyy-MM-dd')},${day.hours.toFixed(2)}`);
  });

  return lines.join('\n');
};
